import React from "react";
import {
    BILLING_DISPLAY_FORMAT,
    EVENT_TYPE,
    pluralise,
    roundUp,
    useEventDataOfSelectedEvent,
} from "@Utils";
import { isInstallmentPaymentType } from "repoV2/utils/common/listing";
import { IPaymentSummary } from "../components/Interfaces";
import Currency from "../components/PaymentSummary/components/CurrencySymbol";
import { useInstalmentsDetailsContext } from "../components/PaymentPlan/utils/InstalmentsDetailsContext";
import { evaluateIsGSTIncluded } from "./evaluateIsGSTIncluded";
import getEventCurrencyForDisplaying from "./getEventCurrencyForDisplaying";

export const unitTypeBasedOnEventType = ({
    eventType,
}: {
    eventType: number;
}) => {
    let _unitTypeBasedOnEventType = "customer";
    if (
        eventType === EVENT_TYPE.APPOINTMENTS ||
        eventType === EVENT_TYPE.WEBINAR
    ) {
        _unitTypeBasedOnEventType = "slot";
    } else if (eventType === EVENT_TYPE.MERCHANDISE) {
        _unitTypeBasedOnEventType = "unit";
    }
    return _unitTypeBasedOnEventType;
};

// handle the payment price for number of items in payment summary
export const useEvaluatePaymentPrice = ({
    numberOfItems,
    eventType,
    earlyBirdObj,
    cgstExclusiveUnitPrice,
    pricePerHead,
    multipleOfferingsSelected,
    unitPrice,
    billingDisplayFormatState,
}: IPaymentSummary.IEvaluatePaymentPrice) => {
    const { eventData } = useEventDataOfSelectedEvent();
    const { selectedPaymentType } = useInstalmentsDetailsContext();
    const isInstallmentSelected = isInstallmentPaymentType(selectedPaymentType);
    const { eventCurrencySymbol } = getEventCurrencyForDisplaying({
        eventData,
    });
    const isGSTIncluded: boolean = evaluateIsGSTIncluded({ eventData });
    let billRowPriceData = {};
    switch (billingDisplayFormatState) {
        case BILLING_DISPLAY_FORMAT.DEFAULT_SHOW_ALL: {
            billRowPriceData = {
                renderLeft: (
                    <>
                        Price for {numberOfItems}&nbsp;
                        {pluralise(
                            unitTypeBasedOnEventType({ eventType }),
                            numberOfItems
                        )}
                        <br />
                        {isGSTIncluded ? "(Inclusive of taxes)" : ""}
                    </>
                ),
                renderRight:
                    earlyBirdObj && !isInstallmentSelected ? (
                        <>
                            <span>
                                <Currency currency={eventCurrencySymbol} />
                                &nbsp;
                                {roundUp(unitPrice * numberOfItems)}
                            </span>
                            &nbsp;
                            <del>
                                <Currency currency={eventCurrencySymbol} />
                                &nbsp;
                                {roundUp(pricePerHead * numberOfItems)}
                            </del>
                        </>
                    ) : (
                        <span>
                            <Currency currency={eventCurrencySymbol} />
                            &nbsp;
                            {roundUp(pricePerHead * numberOfItems)}
                        </span>
                    ),
                hide: multipleOfferingsSelected,
            };
            break;
        }
        case BILLING_DISPLAY_FORMAT.SEPARATE_CGST:
        case BILLING_DISPLAY_FORMAT.PRETEND_CGST_INAPPLICABLE: {
            billRowPriceData = {
                renderLeft: (
                    <>
                        Price for {numberOfItems}&nbsp;
                        {pluralise(
                            unitTypeBasedOnEventType({ eventType }),
                            numberOfItems
                        )}
                    </>
                ),
                renderRight:
                    earlyBirdObj && !isInstallmentSelected ? (
                        <>
                            <span>
                                <Currency currency={eventCurrencySymbol} />
                                &nbsp;
                                {roundUp(
                                    cgstExclusiveUnitPrice * numberOfItems
                                )}
                            </span>
                            &nbsp;
                            <del>
                                <Currency currency={eventCurrencySymbol} />
                                &nbsp;
                                {roundUp(pricePerHead * numberOfItems)}
                            </del>
                        </>
                    ) : (
                        <span>
                            <Currency currency={eventCurrencySymbol} />
                            &nbsp;
                            {roundUp(pricePerHead * numberOfItems)}
                        </span>
                    ),
                hide: multipleOfferingsSelected,
            };
            break;
        }
        default:
            break;
    }
    return billRowPriceData;
};
